import { normalizeOptimizePlatform } from './optimizeCampaignGating';

const STRUCTURED_OPTIMIZER_PLATFORMS = new Set([
  'google-ads-search',
  'google-ads-pmax',
  'google-ads-demandgen',
  'linkedin'
]);
const GOOGLE_ADS_OPTIMIZER_PLATFORMS = new Set([
  'google-ads-search',
  'google-ads-pmax',
  'google-ads-demandgen'
]);

export function usesStructuredOptimizerTemplate(platform) {
  return STRUCTURED_OPTIMIZER_PLATFORMS.has(normalizeOptimizePlatform(platform));
}

export function isGoogleAdsOptimizerPlatform(platform) {
  return GOOGLE_ADS_OPTIMIZER_PLATFORMS.has(normalizeOptimizePlatform(platform));
}

export function canApplyStructuredOptimizerSuggestion(platform) {
  return normalizeOptimizePlatform(platform) === 'google-ads-search';
}

export function getOptimizerPlatformDisplayName(platform) {
  switch (normalizeOptimizePlatform(platform)) {
    case 'google-ads-search': return 'Google Ads Search';
    case 'google-ads-pmax': return 'Google PMax';
    case 'google-ads-demandgen': return 'Google DemandGen';
    case 'linkedin': return 'LinkedIn';
    default: return String(platform || '').toUpperCase();
  }
}

function normalizeEntity(item, type) {
  const id = item.ad_group_id || item.asset_group_id || item.ad_id;
  const name = item.ad_group_name || item.asset_group_name;
  const typeLabel = type === 'creative' ? 'Creative' : type;

  return {
    ...item,
    id,
    name: name || `${typeLabel} ${id || ''}`.trim(),
    metadata: [
      ['Asset Quality', item.asset_quality],
      ['Signal Assessment', item.signal_assessment],
      ['Audience Assessment', item.audience_assessment],
      ['Asset Coverage', item.asset_coverage_summary],
      ['Ad Format', item.ad_format],
      ['Ad Group ID', type === 'creative' ? item.ad_group_id : null]
    ].filter(([, value]) => value)
  };
}

export function getOptimizerAnalysisView(analysis = {}) {
  const safeAnalysis = analysis || {};
  const campaignAnalysis = safeAnalysis.campaign_analysis || {};
  const entitySections = [
    ['Ad Group Analysis', 'Ad Group', safeAnalysis.ad_group_analysis],
    ['Asset Group Analysis', 'Asset Group', safeAnalysis.asset_group_analysis],
    ['Ad Analysis', 'Ad', safeAnalysis.ad_analysis],
    ['Creative Analysis', 'creative', safeAnalysis.creative_analysis]
  ]
    .filter(([, , items]) => Array.isArray(items) && items.length > 0)
    .map(([title, type, items]) => ({
      title,
      items: items.map(item => normalizeEntity(item, type))
    }));

  return {
    campaignAnalysis,
    keyMetrics: campaignAnalysis.key_metrics || campaignAnalysis.performance_metrics,
    keyInsights: campaignAnalysis.key_insights || [],
    campaignAssessments: [
      ['Bidding Assessment', campaignAnalysis.bidding_assessment || campaignAnalysis.bidding_strategy_assessment]
    ].filter(([, value]) => value),
    summary: safeAnalysis.summary,
    recommendations: safeAnalysis.recommendations || [],
    actionableSuggestions: safeAnalysis.actionable_suggestions || [],
    entitySections
  };
}

export function getOptimizerSuggestionView(suggestion = {}) {
  return {
    description: suggestion.description,
    targetEntities: Array.isArray(suggestion.target_entities)
      ? suggestion.target_entities.join(', ')
      : null,
    recommendedValue: suggestion.recommended_value ?? suggestion.suggested_values,
    reasoning: suggestion.reasoning || suggestion.rationale
  };
}

// ── Facebook optimizer reporting_data ───────────────────────────────────────
// details.reporting_data is the raw Meta report the optimizer analyzed:
// { id, name, account_currency, insights: [ {impressions, clicks, ctr, reach,
//   spend, unique_clicks, adset_id, ad_id, ...}, ... ] } — `insights` is an
// ARRAY (one row per ad/adset). Older payloads may carry a single object.

const toNum = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Normalize FB reporting_data into { rows, totals, currency } for display, or
 * null when there are no insights. Totals sum the additive metrics; CTR is
 * recomputed as a PERCENTAGE (clicks/impressions×100 — same convention as the
 * per-row Meta `ctr` field) and CPC from the sums. Reach is intentionally NOT
 * totaled: per-ad reach overlaps, so a sum would double-count people.
 */
export function summarizeFbReportingData(reportingData) {
  const raw = reportingData?.insights;
  const rows = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? [raw] : []);
  if (rows.length === 0) return null;

  const impressions = rows.reduce((s, r) => s + toNum(r.impressions), 0);
  const clicks = rows.reduce((s, r) => s + toNum(r.clicks), 0);
  const spend = rows.reduce((s, r) => s + toNum(r.spend), 0);
  const uniqueClicks = rows.reduce((s, r) => s + toNum(r.unique_clicks), 0);

  return {
    rows,
    currency: reportingData.account_currency || null,
    totals: {
      impressions,
      clicks,
      spend: Math.round(spend * 100) / 100,
      ctr: impressions ? Math.round((clicks / impressions) * 100 * 10000) / 10000 : 0,
      cpc: clicks ? Math.round((spend / clicks) * 100) / 100 : 0,
      unique_clicks: uniqueClicks
    }
  };
}

/**
 * Money label in the ad account's real currency (FB spend is NOT necessarily
 * USD — e.g. INR for Webenza). Falls back to a bare `$` only when the payload
 * carries no account_currency (the pre-existing behavior).
 */
export function formatOptimizerCurrency(value, currency) {
  if (value === null || value === undefined || value === '') return 'N/A';
  const n = toNum(value);
  if (!currency) return `$${n.toFixed(2)}`;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, currencyDisplay: 'narrowSymbol'
    }).format(n);
  } catch (e) {
    return `${currency} ${n.toFixed(2)}`;
  }
}

//: A micros amount, with or without thousands separators. DV360 stores money in
//  millionths of a currency unit, so 9000000 micros IS 9.00 — the two halves of
//  "9.00 (9000000 micros)" say the same thing twice.
const MICROS_AMOUNT = /(\d[\d,]*)\s*micros?/i;
const MICROS_PARENTHETICAL = /\s*\(\s*(\d[\d,]*)\s*micros?\s*\)\s*/i;
const LEADING_MICROS_THEN_PARENS = /^\s*\d[\d,]*\s*micros?\s*\(\s*(.+?)\s*\)\s*$/i;
const ONLY_MICROS = /^\s*(\d[\d,]*)\s*micros?\s*$/i;

/**
 * Drop the micros half of a value label, for display only.
 *
 * The optimizer's own prompt asks for values "converted out of micros", but runs still
 * emit both halves — "9.00 (9000000 micros)", "4500000 micros (4.50 GBP)" — and a
 * column of those is twice as wide and no more informative. This is presentation only:
 * the underlying value and every payload built from it are untouched.
 *
 * Three real shapes, all seen in live exports:
 *   "4.00 (4,000,000 micros)"        -> "4.00"          the parenthetical is redundant
 *   "Fixed Bid: 2.50 (2500000 micros)" -> "Fixed Bid: 2.50"
 *   "4500000 micros (4.50 GBP)"      -> "4.50 GBP"      micros leads; keep the rest
 *   "17000000 micros"                -> "17.00"         micros is ALL there is, so
 *                                                       convert rather than blank it
 */
export function hideMicros(value) {
  if (typeof value !== 'string') return value;
  const raw = value.trim();
  if (!raw || !MICROS_AMOUNT.test(raw)) return value;

  const leading = raw.match(LEADING_MICROS_THEN_PARENS);
  if (leading) return leading[1];

  const only = raw.match(ONLY_MICROS);
  if (only) {
    const units = Number(only[1].replace(/,/g, '')) / 1e6;
    return Number.isFinite(units) ? units.toFixed(2) : value;
  }

  const stripped = raw.replace(MICROS_PARENTHETICAL, ' ').replace(/\s{2,}/g, ' ').trim();
  // Never return an empty cell: if removing the micros leaves nothing, the micros were
  // the whole value and the caller is better served by what it started with.
  return stripped || value;
}
